import { useEffect, useState, type ReactNode } from 'react';
import type { DialogInitialFocus } from './DialogBase';
import { Modal } from './Modal';
import { Sheet } from './Sheet';

/**
 * Steuert, welche Darstellungsform unabhaengig vom Viewport erzwungen werden kann.
 * - 'auto'  → reaktives Verhalten (Sheet < breakpoint, Modal >= breakpoint) — Default.
 * - 'sheet' → immer Sheet, unabhaengig von der Breite.
 * - 'modal' → immer Modal, unabhaengig von der Breite.
 */
export type DialogPresentation = 'auto' | 'sheet' | 'modal';

export interface ResponsiveDialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  footer?: ReactNode;
  closable?: boolean;
  maxWidth?: number | string;
  /** Startfokus: Ref-Ziel, Dialog-Container ('none') oder bisheriger Auto-Default. */
  initialFocus?: DialogInitialFocus;
  initialSheetExpanded?: boolean;
  breakpoint?: number;
  /** Darstellungsform. Default 'auto' → heutiges Breakpoint-Verhalten bleibt erhalten. */
  presentation?: DialogPresentation;
  /** Optionale Vordergrund-Ebene fuer Dialoge ueber blockierenden App-Gates. */
  layer?: 'default' | 'foreground';
}

function getInitialNarrowState(breakpoint: number): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
  }
  return window.innerWidth < breakpoint;
}

function useNarrowDialog(breakpoint: number): boolean {
  const [isNarrow, setIsNarrow] = useState(() => getInitialNarrowState(breakpoint));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const query = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handleChange = (event: MediaQueryListEvent) => setIsNarrow(event.matches);

    setIsNarrow(query.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, [breakpoint]);

  return isNarrow;
}

export function ResponsiveDialog({
  breakpoint = 600,
  maxWidth,
  presentation = 'auto',
  children,
  ...dialogProps
}: ResponsiveDialogProps) {
  // Q5 (E0/Paket G): Umschaltpunkt 744 -> 600 (Gleichschritt mit Q9). Slide Over
  // (320) und schmale Split-View-Fenster (bis 599) bekommen das Sheet, alles
  // darueber das zentrierte Modal — kein 1000-px-Textzeile mehr auf dem iPad.
  const isNarrow = useNarrowDialog(breakpoint);

  // Im 'auto'-Modus entscheidet der Hook; bei 'sheet'/'modal' wird er ueberschrieben.
  const showSheet = presentation === 'sheet' || (presentation === 'auto' && isNarrow);

  if (showSheet) {
    // maxWidth gilt jetzt auch im Sheet-Zweig (war vorher nur ans Modal gereicht).
    return <Sheet {...dialogProps} maxWidth={maxWidth}>{children}</Sheet>;
  }

  return (
    <Modal {...dialogProps} maxWidth={maxWidth}>
      {children}
    </Modal>
  );
}
